import { Link, router } from '@inertiajs/react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDeleteLeft, faPenToSquare, faPhotoFilm, faPlus } from "@fortawesome/free-solid-svg-icons";
import { formatDateTime } from "../../Announcements/dates";
import RecordsTable, { BulkAction } from '../Attachments/RecordsTable';
import { handleDelete } from './Index';

// Handle bulk delete
const handleBulkDelete = (selectedRows: any[], clearSelection: () => void) => {
  const count = selectedRows.length;
  const message = count === 1
    ? `Are you sure you want to delete 1 group? This action cannot be undone.`
    : `Are you sure you want to delete ${count} groups? This action cannot be undone.`;

  if (window.confirm(message)) {
    const ids = selectedRows.map(row => row.id);
    router.delete('/admin/attachments/groups', {
      data: { ids },
      onFinish: () => {
        clearSelection();
      }
    });
  }
};

// Header actions component for groups
function GroupsHeaderActions() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      gap: '4px',
    }}>
      <Link
        className="link link-primary"
        style={{ cursor: 'pointer' }}
        href="/admin/attachments"
        preserveState={true}
      >
        <button
          className="btn btn-sm btn-info"
        >
          Attachments
          <FontAwesomeIcon icon={faPhotoFilm} />
        </button>
      </Link>
      <Link
        className="link link-primary"
        style={{ cursor: 'pointer' }}
        href="/admin/attachments/groups"
        preserveState={true}
        only={['previewed_group']}
      >
        <button
          className="btn btn-sm btn-success btn-primary"
        >
          New
          <FontAwesomeIcon icon={faPlus} />
        </button>
      </Link>
    </div>
  );
}

// Bulk actions for groups
const groupsBulkActions: BulkAction[] = [
  {
    label: 'Delete',
    className: 'btn btn-sm btn-error',
    onClick: handleBulkDelete
  }
];

interface AttachmentGroupsTableProps {
  attachmentGroups: {
    data: any[];
    metadata: {
      total: number;
      has_more: boolean;
      query?: string;
      order_by?: string;
      order?: 'asc' | 'desc';
    };
  };
  previewedGroup?: any;
}

export default function AttachmentGroupsTable({
  attachmentGroups,
  previewedGroup
}: AttachmentGroupsTableProps) {
  const handleSortChange = (columnName: string, direction: 'asc' | 'desc' | null) => {
    router.visit('', {
      data: {
        order_by: columnName,
        order: direction
      },
      preserveState: true,
      preserveScroll: true
    })
  };

  const headers = [
    {
      name: "id",
      label: "ID"
    },
    {
      name: "title",
      label: "Title",
      renderCell: (row: any) => (
        <div style={{
          minWidth: '150px',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}>
          <Link
            href={`/admin/attachments/groups/${row.id}`}
            className="link link-primary"
            preserveState={true}
          >
            {row.title}
          </Link>
        </div>
      )
    },
    {
      name: "description",
      label: "Description",
      renderCell: (row: any) => (
        <div style={{
          minWidth: '200px',
          maxWidth: '300px',
          overflow: 'hidden',
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        }}>
          {row.description || '-'}
        </div>
      )
    },
    {
      name: "show_on_homepage",
      label: "Show on Homepage",
      renderCell: (row: any) => (
        <div>
          {row.show_on_homepage ? 'Yes' : 'No'}
        </div>
      )
    },
    {
      name: "record_attachments_count",
      label: "Attachments"
    },
    {
      name: "created_at",
      label: "Created",
      headerStyles: {
        minWidth: '150px'
      },
      renderCell: (row: any) => (
        <div>
          {formatDateTime(row.created_at)}
        </div>
      )
    },
    {
      name: "updated_at",
      label: "Updated",
      headerStyles: {
        minWidth: '150px'
      },
      renderCell: (row: any) => (
        <div>
          {formatDateTime(row.updated_at)}
        </div>
      ),
      renderCellActions: (row: any) => (
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '4px',
        }}>
          <Link
            className="btn btn-sm btn-info"
            href={`/admin/attachments/groups/${row.id}`}
            preserveState={true}
            only={['previewed_group']}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
          </Link>
          <div
            className="btn btn-sm btn-error"
            onClick={() => handleDelete(row.id, row.title)}
          >
            <FontAwesomeIcon icon={faDeleteLeft} />
          </div>
        </div>
      )
    },
  ];

  return (
    <RecordsTable
      headers={headers}
      data={attachmentGroups.data}
      hasMore={attachmentGroups.metadata.has_more}
      recordsCount={attachmentGroups.metadata.total}
      recordsCountLabel={attachmentGroups.metadata.total === 1 ? 'group' : 'groups'}
      onSortChange={handleSortChange}
      highlightedRowId={previewedGroup ? previewedGroup.id : null}
      searchQuery={attachmentGroups.metadata.query}
      sortColumn={attachmentGroups.metadata.order_by}
      sortDirection={attachmentGroups.metadata.order}
      title="Groups"
      infiniteScrollDataKey="attachment_groups"
      headerActions={<GroupsHeaderActions />}
      bulkActions={groupsBulkActions}
    />
  )
}